/** @jsxImportSource @emotion/react */

import type { ComponentPropsWithRef, ElementType } from "react";
import { cloneElement, forwardRef, useRef } from "react";
import { getValidChildren } from "../utils";
import { useTabContext } from "./Tab.provider";
import { getTabARIA } from "./Tab.utils";
import type { TabPanelProps, TabPanelsProps } from "./Tabs.types";

export const TabPanels = (props: TabPanelsProps) => {
  const renderTabPanels = () =>
    getValidChildren(props.children).map((child, index) =>
      cloneElement(child, {
        // eslint-disable-next-line react/no-array-index-key
        key: index,
        _index: index,
      }),
    );

  return <>{renderTabPanels()}</>;
};

type TabPanelComponent = <E extends ElementType = "section">(
  props: TabPanelProps<E> & {
    ref?: ComponentPropsWithRef<E>["ref"];
  },
) => JSX.Element | null;

export const TabPanel: TabPanelComponent = forwardRef(function TabPanel<E extends ElementType>(
  props: TabPanelProps<E>,
  ref: ComponentPropsWithRef<E>["ref"],
) {
  const isFirstOpen = useRef(true);
  const Element = props.as ?? "section";
  const tabContext = useTabContext();

  const attr = getTabARIA({
    index: props._index,
    uniqueId: tabContext.uniqueId,
  });

  if (!tabContext) {
    return null;
  }

  const isActive = tabContext.activeIndex === props._index;
  const shouldRender = isActive || props.preRender || isFirstOpen.current === false;

  if (isActive && isFirstOpen.current) isFirstOpen.current = false;

  return (
    <Element
      role="tabpanel"
      tabIndex={0}
      ref={ref}
      className={props.className}
      css={props.sx}
      {...props.native}
      {...attr.tabpanel}
      {...(!isActive && { hidden: true })}
    >
      {shouldRender ? props.children : null}
    </Element>
  );
});
